import { gql } from '@apollo/client';
import { BarChart10Icon, BillIcon, CalendarIcon, ClockFastForwardIcon, DollarIcon } from '@orn/shared-web/icons';
import formatAmountRangeLabel from '@orn/shared-web/utils/money/formatAmountRangeLabel';
import toCurrency from '@orn/shared-web/utils/money/toCurrency';

import { RecurringTransactionFrequency, RecurringTransactionType } from '../../../__generated__/globalTypes';
import { formatExpectedDaysUntil } from '../../../utils/formatting';
import { calculateYearlySpend, formatRecurringType, getFrequencyLabel } from '../../../utils/spending/recurring';
import { toAmountRangeBarValues } from '../../../utils/spending/transactionStats';
import AmountRangeBar, { AMOUNT_RANGE_BAR_HEIGHT_PX } from '../../charts/AmountRangeBar';
import RecordPane from '../../entity/RecordPane';

import type { RecurringStreamSectionFragment } from './__generated__/RecurringStreamSectionBase.gql';
import type { TransactionStats } from '../../../hooks/queries/useTransactionStats';

const RECURRING_STREAM_SECTION_FRAGMENT = gql`
  fragment RecurringStreamSection on RecurringTransaction {
    id
    type
    frequency
    nextOccurrence
    isActive
    amountRange
    currencyCode
    merchant {
      id
    }
  }
`;

const SECTION_TITLES: Record<RecurringTransactionType, string> = {
  [RecurringTransactionType.Subscription]: 'Subscription details',
  [RecurringTransactionType.Bill]: 'Bill overview',
  [RecurringTransactionType.Income]: 'Income details',
};

export interface IRecurringStreamSectionBaseProps {
  recurringTransaction: RecurringStreamSectionFragment;
  /** All-time stats over the stream's own payments. `null` drops the bar; the rows stay. */
  stats: TransactionStats | null;
  /** Reserves the bar's cell with a skeleton while the stats read is in flight. */
  loading: boolean;
  /** The record's amount as the bar's current marker. `null` drops the bar. */
  currentAmount: number | null;
  /** Where "View merchant" goes. `null` hides the link. */
  viewMerchantPath: string | null;
  className?: string;
}

/**
 * The stream section of a record page (Figma "Transaction/Card/Subscription
 * details" and "Bill Overview"), titled by the stream's type: an
 * `AmountRangeBar` over the stream's own payments, then Type, Frequency,
 * Amount, Next expected, and Estimated per year rows. Pure;
 * `RecurringStreamSection` feeds it from `getTransactionStats`.
 *
 * The rows come from the fragment, so they render whatever the stats do. Next
 * expected is omitted for an inactive stream or one with no prediction, and
 * the yearly estimate for a one-time charge.
 */
const RecurringStreamSectionBase = ({
  recurringTransaction,
  stats,
  loading,
  currentAmount,
  viewMerchantPath,
  className,
}: IRecurringStreamSectionBaseProps) => {
  const { type, frequency, nextOccurrence, isActive, amountRange, currencyCode } = recurringTransaction;
  const amountLabel = formatAmountRangeLabel(amountRange, currencyCode);
  const nextExpected = isActive && nextOccurrence ? formatExpectedDaysUntil(nextOccurrence) : null;
  const yearlyEstimate =
    frequency === RecurringTransactionFrequency.OneTime
      ? null
      : toCurrency(Math.abs(calculateYearlySpend(amountRange, frequency)), { currencyCode });

  return (
    <RecordPane.Section
      title={SECTION_TITLES[type]}
      link={viewMerchantPath ? { label: 'View merchant', to: viewMerchantPath } : undefined}
      className={className}
    >
      {currentAmount !== null && stats && (
        <div className="px-5 py-8">
          <AmountRangeBar
            {...toAmountRangeBarValues(stats)}
            current={currentAmount}
            currencyCode={stats.currencyCode}
          />
        </div>
      )}
      {currentAmount !== null && !stats && loading && <BarSkeleton />}
      <RecordPane.Row icon={<BillIcon />} label="Type">
        {formatRecurringType(type)}
      </RecordPane.Row>
      <RecordPane.Row icon={<ClockFastForwardIcon />} label="Frequency">
        {getFrequencyLabel(frequency)}
      </RecordPane.Row>
      {amountLabel && (
        <RecordPane.Row icon={<DollarIcon />} label="Amount">
          Typically {amountLabel}
        </RecordPane.Row>
      )}
      {nextExpected && (
        <RecordPane.Row icon={<CalendarIcon />} label="Next expected">
          <span className="lowercase">{nextExpected}</span>
        </RecordPane.Row>
      )}
      {yearlyEstimate && (
        <RecordPane.Row icon={<BarChart10Icon />} label="Estimated per year">
          {yearlyEstimate}
        </RecordPane.Row>
      )}
    </RecordPane.Section>
  );
};

const BarSkeleton = () => {
  return (
    <div className="px-5 py-8" aria-busy="true" aria-label="Loading payment range">
      <div className="bg-tertiary animate-pulse rounded-md" style={{ height: AMOUNT_RANGE_BAR_HEIGHT_PX }} />
    </div>
  );
};

export default Object.assign(RecurringStreamSectionBase, { Fragment: RECURRING_STREAM_SECTION_FRAGMENT });
